// Доступ к Lichess Opening Explorer через наш прокси (Worker /v1/explorer):
// анонимам explorer.lichess.ovh отдаёт 401, поэтому ходим на api.getchesscoach.com,
// он подставляет OAuth-токен и кэширует сутки. Реальная сигнатура (проверено curl):
//   GET /v1/explorer?ratings=1400,1600,1800&fen=<urlencoded FEN>
//   → { white, draws, black, moves:[{uci,san,white,draws,black,opening?}], ... }
// Поверх сетевого кэша держим LRU в localStorage (ответы дороги, позиции дебюта
// повторяются между партиями) + in-memory на сессию.
import type { ExplorerData } from '@ext/core/openings-study';

const API_BASE = 'https://api.getchesscoach.com';
const LS_KEY = 'cc_explorer_lru';
const MEM_MAX = 200;
const LS_MAX = 120; // сколько ответов держим в localStorage

const mem = new Map<string, ExplorerData>();

/** Рейтинговая корзина под уровень игрока (как в extension/opening-explorer). */
function ratingBuckets(rating: number): string {
  if (rating < 1400) return '1000,1200,1400';
  if (rating < 1800) return '1400,1600,1800';
  return '1800,2000,2200';
}

function keyOf(fen: string, buckets: string): string {
  // позиция дебюта не зависит от счётчиков полуходов — ключ по placement+turn+права+ep
  const p = fen.trim().split(/\s+/);
  return `${p[0]}|${p[1] || 'w'}|${p[2] || '-'}|${p[3] || '-'}|${buckets}`;
}

type LruStore = Record<string, { t: number; d: ExplorerData }>;

function readLs(): LruStore {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || '{}') as LruStore;
  } catch {
    return {};
  }
}

function writeLs(store: LruStore): void {
  const keys = Object.keys(store);
  if (keys.length > LS_MAX) {
    // выкидываем самые старые по времени доступа
    keys
      .sort((a, b) => store[a]!.t - store[b]!.t)
      .slice(0, keys.length - LS_MAX)
      .forEach((k) => delete store[k]);
  }
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(store));
  } catch {
    /* webview may block storage — сетевой кэш всё равно спасёт */
  }
}

function lsGet(key: string): ExplorerData | null {
  const store = readLs();
  const hit = store[key];
  if (!hit) return null;
  hit.t = Date.now(); // bump recency
  writeLs(store);
  return hit.d;
}

function lsPut(key: string, data: ExplorerData): void {
  const store = readLs();
  store[key] = { t: Date.now(), d: data };
  writeLs(store);
}

function memPut(key: string, data: ExplorerData): void {
  if (mem.size >= MEM_MAX) {
    const oldest = mem.keys().next().value;
    if (oldest) mem.delete(oldest);
  }
  mem.set(key, data);
}

/**
 * Явка позиции по нашему прокси. Возвращает null при сетевой ошибке / пустом теле.
 * Кэш: mem → localStorage → сеть. rating задаёт корзину (по умолчанию любительскую).
 */
export async function fetchExplorer(fen: string, rating = 1400, signal?: AbortSignal): Promise<ExplorerData | null> {
  const buckets = ratingBuckets(rating);
  const key = keyOf(fen, buckets);
  const cached = mem.get(key) ?? lsGet(key);
  if (cached) {
    memPut(key, cached);
    return cached;
  }
  const params = new URLSearchParams({ ratings: buckets, fen });
  try {
    const res = await fetch(`${API_BASE}/v1/explorer?${params}`, { signal, credentials: 'omit' });
    if (!res.ok) return null;
    const data = (await res.json()) as ExplorerData;
    if (!data || !Array.isArray(data.moves)) return null;
    memPut(key, data);
    lsPut(key, data);
    return data;
  } catch {
    return null;
  }
}
